import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Search } from "lucide-react";
import api, { formatDate } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";

const CAT_LABELS = {
  rozpis: "Rozpis súťaží",
  pravidla: "Pravidlá futbalu",
  registracne: "Registračné dokumenty",
  tlaciva: "Tlačivá",
  smernice: "Smernice",
};

export default function Documents() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("cat") || "all";
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState(initialCat);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");

  useEffect(() => {
    api.get("/documents").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const cats = useMemo(() => ["all", ...Object.keys(CAT_LABELS)], []);
  const years = useMemo(() => {
    const set = new Set(items.map((d) => (d.date || "").slice(0, 4)).filter(Boolean));
    return ["all", ...Array.from(set).sort((a, b) => b.localeCompare(a))];
  }, [items]);
  const filtered = items.filter((d) =>
    (cat === "all" || d.category === cat) &&
    (year === "all" || (d.date || "").slice(0, 4) === year) &&
    (!q || d.name.toLowerCase().includes(q.toLowerCase()))
  );

  const handleCat = (c) => {
    setCat(c);
    if (c === "all") setParams({});
    else setParams({ cat: c });
  };

  return (
    <div data-testid="documents-page">
      <Seo title="Dokumenty" description="Rozpis súťaží, pravidlá futbalu, registračné dokumenty, tlačivá a smernice ObFZ Levice na stiahnutie." />
      <PageHeader
        eyebrow="Tlačivá & smernice"
        title="Dokumenty"
        description="Rozpis súťaží, pravidlá futbalu, registračné dokumenty, tlačivá, zápisnice a smernice ObFZ Levice."
        breadcrumbs={[{ label: "Dokumenty" }]}
      />

      <section className="section-pad bg-paper">
        <div className="wrap">
          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Hľadať dokument..." className="input pl-9"
                     data-testid="search-documents" />
            </div>
            <div className="relative">
              <select value={year} onChange={(e) => setYear(e.target.value)}
                      className="input pr-8 mono text-[11px] font-bold uppercase tracking-wider cursor-pointer"
                      data-testid="doc-year-filter" aria-label="Filter podľa roku">
                {years.map((y) => (
                  <option key={y} value={y}>{y === "all" ? "Všetky roky" : y}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-1 overflow-x-auto no-scrollbar">
              {cats.map((c) => (
                <button key={c} type="button" onClick={() => handleCat(c)}
                        className={`px-3 py-2 mono text-[11px] font-bold uppercase tracking-wider border transition-colors flex-shrink-0 ${
                          cat === c ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-700 border-ink-200 hover:border-ink-900"
                        }`}
                        data-testid={`doc-cat-${c}`}>
                  {c === "all" ? "Všetky" : CAT_LABELS[c]}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-ink-100 divide-y divide-ink-100">
            {filtered.length === 0 && <div className="p-10 text-center text-ink-500 text-sm">Žiadne dokumenty nezodpovedajú filtru.</div>}
            {filtered.map((d, i) => (
              <a key={d.id} href={d.file_url} target="_blank" rel="noopener noreferrer"
                 className="grid grid-cols-12 items-center gap-4 px-5 py-4 hover:bg-cream transition-colors group"
                 data-testid={`document-${d.id}`}>
                <div className="col-span-1 mono text-[11px] font-bold text-ink-400">{String(i + 1).padStart(2, "0")}</div>
                <div className="col-span-7 md:col-span-8">
                  <div className="font-heading font-bold text-ink-900 group-hover:text-green-700 transition-colors">{d.name}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-ink-400 mt-0.5">
                    {CAT_LABELS[d.category] || d.category} · {d.size || "—"}
                  </div>
                </div>
                <div className="col-span-3 md:col-span-2 mono text-xs text-ink-500 text-right">{formatDate(d.date)}</div>
                <div className="col-span-1 text-right">
                  <Download className="w-4 h-4 text-ink-400 inline group-hover:text-green-700" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
